import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Stack } from 'react-bootstrap';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../context/ToastContext';
import { useDeleteKitchenSkillMutation } from '../../../services/endpoints/content/kitchenSkills';
import { Document } from '../../../types/content.type';
import { getErrorMessage } from '../../utils';

const DeleteKitchenSkillButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteKitchenSkill, { isLoading }] = useDeleteKitchenSkillMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteKitchenSkill(id).unwrap();
      addToast('Kitchen skill deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Kitchen Skill"
        message="Are you sure you want to delete this kitchen skill?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

export const kitchenSkillsColumns: ColumnDef<Document>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/content/kitchen-skills/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Description',
    accessorKey: 'description'
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/content/kitchen-skills/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Edit"
          url={`/admin/content/kitchen-skills/${row.original.id}/edit`}
          underline
        />
        <DeleteKitchenSkillButton id={row.original.id} />
      </Stack>
    )
  }
];
